import { Configure, InstantSearch, useSortBy } from 'react-instantsearch';
import {
  generateBaseProductIndexName,
  generateProductIndexName,
  getChannelFromLocale,
} from '@monofront/business/utils';

import { ALGOLIA_PRODUCT_SORT_BY } from '@monofront/business/constants';
import Autocomplete from './Autocomplete';
import getConfig from 'next/config';
import { searchClient } from '@services/businessContainer';
import { useEnrichAlgoliaContexts } from '@hooks/businessContainer';
import { useRouter } from 'next/router';
import { ALGOLIA_CONTEXT_AUTOCOMPLETE_V2 } from '@monofront/business/hooks';
import { useCacheFirstAuthentication } from '@context/persistedAuthContext';
import { OneTrustCookieCategories } from '@components/Vendors/Vendors';
import getCookieConsent from '@utils/getCookieConsent';

const {
  publicRuntimeConfig: { algoliaConfig },
} = getConfig();

const VirtualSortBy = ({ sortIndexName }: { sortIndexName: string }) => {
  useSortBy({ items: [{ label: 'default', value: sortIndexName }] });

  return null;
};

// From the doc https://www.algolia.com/doc/guides/building-search-ui/going-further/conditional-requests/react/#detecting-empty-search-requests
const instantSearchClient: typeof searchClient = {
  ...searchClient,
  search(requests) {
    if (
      Array.isArray(requests) &&
      requests.every(({ params }) => !params?.query)
    ) {
      return Promise.resolve({
        results: requests.map(() => ({
          hits: [],
          nbHits: 0,
          nbPages: 0,
          page: 0,
          processingTimeMS: 0,
          hitsPerPage: 0,
          exhaustiveNbHits: false,
          query: '',
          params: '',
        })),
      });
    }

    return searchClient.search(requests);
  },
};

interface IGlobalSearchProps {
  handleInputFocus?: () => void;
  handleInputBlur?: () => void;
}

const GlobalSearch: React.FC<IGlobalSearchProps> = ({
  handleInputFocus,
  handleInputBlur,
}) => {
  const contexts = useEnrichAlgoliaContexts([ALGOLIA_CONTEXT_AUTOCOMPLETE_V2]);
  const { locale } = useRouter();
  const { customer } = useCacheFirstAuthentication();

  const baseIndexName =
    generateBaseProductIndexName({
      environment: algoliaConfig.environment,
      app: false,
      channel: getChannelFromLocale(locale),
    }) + '_autocomplete';
  const sortIndexName = generateProductIndexName(
    algoliaConfig.environment,
    false,
    getChannelFromLocale(locale),
    true,
    ALGOLIA_PRODUCT_SORT_BY.POPULARITY,
  );

  return (
    <InstantSearch
      searchClient={instantSearchClient}
      initialUiState={{
        [baseIndexName]: {
          sortBy: sortIndexName,
        },
      }}
      indexName={baseIndexName}
      future={{
        preserveSharedStateOnUnmount: true,
      }}
    >
      <Configure
        ruleContexts={contexts}
        userToken={
          getCookieConsent(OneTrustCookieCategories.FUNCTIONALITY)
            ? customer?.uid
            : undefined
        }
      />
      <Autocomplete
        handleInputFocus={handleInputFocus}
        handleInputBlur={handleInputBlur}
      />
      <VirtualSortBy sortIndexName={sortIndexName} />
    </InstantSearch>
  );
};

export default GlobalSearch;
